import { useMemo } from 'react';
import { DesenhoDeQr } from './DesenhoDeQr.js';
import './BlocoQr.css';

type Props = {
  /**
   * Texto que vai dentro do QR — o JSON compacto do pareamento.
   *
   * É também o que a tela oferece para DIGITAR: o app aceita este conteúdo e
   * mais nada. Ver o comentário do bloco de digitação, abaixo.
   */
  readonly conteudo: string;
  /** Instante em que o código morre (ISO). */
  readonly expiraEm: string;
  /** Nome do desenho para o leitor de tela. */
  readonly descricao: string;
};

const HORARIO = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Bloco do QR de pareamento (RF-003, UC12).
 *
 * O desenho é gerado **aqui, no navegador**, a partir do texto que o
 * middleware devolveu: nenhuma requisição sai do painel para gerar imagem
 * (RNF-026) — e mandar um código de pareamento para um serviço de terceiros
 * seria entregar a credencial do aparelho de bandeja.
 *
 * O SVG usa `currentColor`: a cor sai do token da folha de estilo, e o QR
 * acompanha o tema sem nenhum valor de cor escrito no componente.
 */
export function BlocoQr({ conteudo, expiraEm, descricao }: Props) {
  const validade = useMemo(() => {
    const instante = new Date(expiraEm);
    return Number.isNaN(instante.getTime()) ? null : HORARIO.format(instante);
  }, [expiraEm]);

  return (
    <div className="bloco-qr">
      <DesenhoDeQr
        conteudo={conteudo}
        descricao={descricao}
        classe="bloco-qr__desenho"
        mensagemDeFalha="Não foi possível desenhar o QR deste pareamento. Use o conteúdo abaixo."
      />

      {/*
        O que aparece aqui é o CONTEÚDO do QR, e não o código curto.

        A tela exibia o código sozinho e mandava digitá-lo quando a câmera
        falhasse. Só que o app recusa: o código não carrega o endereço da API
        nem o tenant, e um coletor ainda não pareado não tem de onde tirá-los —
        é justamente por isso que eles viajam dentro do QR. Quem seguia a
        instrução da tela levava "Este código não é um QR de pareamento" do
        outro lado.

        Digitar isto à mão é ruim, e a tarefa F1.4b existe para resolver com um
        formato curto nas DUAS pontas. Enquanto ela não vem, a tela oferece o
        que funciona em vez do que é confortável.
      */}
      <p className="bloco-qr__rotulo-conteudo">Para digitar no coletor, sem a câmera:</p>
      <p className="bloco-qr__conteudo">{conteudo}</p>

      <p className="bloco-qr__validade">
        {validade === null ? 'Código de uso único.' : `Válido até ${validade}. Vale uma única vez.`}
      </p>
    </div>
  );
}
